import { useEffect, useMemo, useState } from "react";
import { getPeriods } from "../api/client";

/**
 * ماهِ محاسبهٔ حقوق — گزینه‌های مشترکِ ثبت برش و مدیریت تولید.
 *
 * کارِ یک پارت ممکن است در خرداد انجام شده باشد ولی حقوقش در تیر بنشیند؛
 * این انتخاب همان «ماهِ محاسبه» است، جدا از تاریخِ کار.
 * مقدارِ خالی یعنی «طبقِ تاریخِ کار».
 */
export const JALALI_MONTHS = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];

/** سالِ جاریِ شمسی (اگر مرورگر تقویمِ فارسی نداشت، ۱۴۰۵). */
export function currentJalaliYear() {
  try {
    const y = Number(new Intl.DateTimeFormat("en-u-ca-persian", { year: "numeric" })
      .format(new Date()).replace(/\D/g, ""));
    return Number.isFinite(y) && y > 1300 ? y : 1405;
  } catch {
    return 1405;
  }
}

/** «۱۴۰۵-۴» → «تیر ۱۴۰۵» — سالِ جاری و سالِ قبل کافی است. */
export const PAYROLL_MONTH_OPTIONS = (() => {
  const y = currentJalaliYear();
  const out = [];
  for (const yy of [y, y - 1]) {
    for (let m = 1; m <= 12; m += 1) {
      out.push({ value: `${yy}-${m}`, label: `${JALALI_MONTHS[m - 1]} ${yy}` });
    }
  }
  return out;
})();

/** «۱۴۰۵-۴» → { payroll_year: 1405, payroll_month: 4 }؛ خالی → هر دو null. */
export function splitPayrollMonth(value) {
  if (!value) return { payroll_year: null, payroll_month: null };
  const [y, m] = String(value).split("-").map(Number);
  return { payroll_year: y || null, payroll_month: m || null };
}

/** «تیر ۱۴۰۵» — ارقامِ فارسی، یک‌جا برای هر صفحه‌ای که ماهِ محاسبه را نشان می‌دهد. */
export function payrollMonthLabel(year, month) {
  if (!year || !month) return null;
  return `${JALALI_MONTHS[month - 1]} ${String(year).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d])}`;
}

/** مقدارِ ذخیره‌شده → مقدارِ Select. */
export function joinPayrollMonth(year, month) {
  return year && month ? `${year}-${month}` : undefined;
}

/* v2.152.12 (کاربر ۱۴۰۵/۰۸/۰۳): «وقتی حقوقِ یک ماه بسته شد، دیگر نتوانیم آن ماه را به‌عنوانِ ماهِ محاسبه انتخاب کنیم».
   ماه‌های بسته (تأیید/قفل/پرداخت) یک‌بار از دوره‌ها خوانده می‌شوند و در لیستِ انتخاب «— بسته شد» و غیرفعال‌اند.
   سرور هم همین را رد می‌کند (`assert_payroll_month_open`)؛ اینجا فقط زودتر و روشن گفته می‌شود. */
let _closedCache = null;
let _closedPending = null;
const isClosedPeriod = (p) => ["approved", "paid", "locked"].includes(p.status) || p.closing_status === "locked";

export function useClosedPayrollMonths() {
  const [closed, setClosed] = useState(_closedCache || new Set());
  useEffect(() => {
    if (_closedCache) return undefined;
    let alive = true;
    _closedPending = _closedPending || getPeriods()
      .then((r) => { _closedCache = new Set((r.data || []).filter(isClosedPeriod).map((p) => `${p.year}-${p.month}`)); return _closedCache; })
      .catch(() => new Set());
    _closedPending.then((s) => { if (alive) setClosed(s); });
    return () => { alive = false; };
  }, []);
  return closed;
}

export function payrollMonthOptions(closed) {
  if (!closed || !closed.size) return PAYROLL_MONTH_OPTIONS;
  return PAYROLL_MONTH_OPTIONS.map((o) => (closed.has(o.value)
    ? { ...o, disabled: true, label: `${o.label} — بسته شد` } : o));
}

/** همان گزینه‌ها با ماه‌های بسته‌ی غیرفعال — برای صفحه‌ها */
export function usePayrollMonthOptions() {
  const closed = useClosedPayrollMonths();
  return useMemo(() => payrollMonthOptions(closed), [closed]);
}
